import { saveRequestType, type CatalogRequestType } from "@/lib/catalog-api";
import { useConfiguration } from "./useConfiguration";
import { saveErrorMessage } from "../lib/saveMessage";
import { useToast } from "./useToast";

/**
 * Request types save as one bundle, in list order.
 * The first failure stops the bundle and names what was already saved.
 * "Default coverage" sends null instead of the path open on the page.
 */
export function useSaveRequestTypes() {
  const {
    requestTypes,
    setSaving,
    markRequestsSaved,
  } = useConfiguration();
  const toast = useToast();

  const save = async () => {
    setSaving(true);
    const savedNames: string[] = [];
    let list = requestTypes;
    try {
      for (let index = 0; index < list.length; index += 1) {
        const requestType = list[index];
        let result: {
          ok: boolean;
          message?: string;
          requestType?: CatalogRequestType;
        };
        try {
          result = await saveRequestType(
            {
              code: requestType.code,
              title: requestType.title,
              confirmationTemplate: requestType.confirmationTemplate,
              isPatientSelectable: requestType.isPatientSelectable,
              isEnabled: requestType.isEnabled,
              sortOrder: requestType.sortOrder,
              escalationPolicyId: requestType.escalationPolicyId ?? null,
              iconKey: requestType.iconKey ?? null,
              priority: requestType.priority ?? "Routine",
            },
            requestType.requestTypeId > 0
              ? requestType.requestTypeId
              : undefined,
          );
        } catch (error) {
          result = { ok: false, message: saveErrorMessage(error) };
        }

        if (!result.ok) {
          markRequestsSaved(list, index, false);
          const reason = saveErrorMessage(result.message);
          const failedName = requestType.title || requestType.code;
          toast.error(
            savedNames.length === 0
              ? `Nothing in this bundle was saved. ${reason}`
              : `Saved ${savedNames.join(", ")}. ${failedName} failed: ${reason}. Remaining request types were not sent.`,
          );
          return;
        }

        if (result.requestType) {
          const saved = result.requestType;
          list = list.map((item, itemIndex) =>
            itemIndex === index ? saved : item,
          );
        }
        savedNames.push(requestType.title || requestType.code);
      }

      markRequestsSaved(list, list.length, true);
      toast.success("All request types saved.");
    } finally {
      setSaving(false);
    }
  };

  return { save };
}
